import React, { useEffect } from 'react';
import { Grid2 as Grid, Paper, Typography, SvgIconTypeMap, Divider, Tooltip, IconButton, Box } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { AddCircleOutlined, Campaign, CrisisAlert, Event, Handyman, LocalParking, OpenInFull } from '@mui/icons-material';
import { announcementsInfo, currentBookings, ongoingServiceRequests, reportedViolations, visitorParkingRequests } from '../../Data/DummyData';
import { useNavigate } from 'react-router-dom';
import { Announcements, Booking, ViewServiceRequestData, Violation, VisitorParkingRequest } from '../../Types/UnitFeatureTypes';
import { useDispatch, useSelector } from 'react-redux';
import { Actions } from '../../Redux/Reducer/rootReducer';
import BuildAnnouncementTable from '../BuildTables/BuildAnnouncementTable';
import BuildBookingsTable from '../BuildTables/BuildBookingsTable';
import BuildServiceRequestsTable from '../BuildTables/BuildServiceRequestsTable';
import BuildViolationsTable from '../BuildTables/BuildViolationsTable';
import BuildVisitorParkingTable from '../BuildTables/BuildVisitorParkingTable';
import { fetchAnnouncement, fetchBooking, fetchServiceRequest, fetchViolation, fetchVisitorParking } from '../../Redux/selectors';
import { useAuth } from '../../context/authContext';


const Dashboard: React.FC = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const allAnnouncements = useSelector(fetchAnnouncement) as Announcements[];
  const allBookings = useSelector(fetchBooking) as Booking[];
  const allServiceRequests = useSelector(fetchServiceRequest) as ViewServiceRequestData[];
  const allViolations = useSelector(fetchViolation) as Violation[];
  const allVisitorParking = useSelector(fetchVisitorParking) as VisitorParkingRequest[];
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    dispatch(Actions.fetchUnit.unitActions.request({}));
    dispatch(Actions.fetchAnnouncement.announcementActions.request({}));
    dispatch(Actions.fetchBooking.bookingActions.request({}));
    dispatch(Actions.fetch.serviceRequestActions.request({}));
    dispatch(Actions.fetchViolation.violationActions.request({}));
    dispatch(Actions.fetchVisitorParking.visitorParkingActions.request({}));
  }, []);


  enum TableHeaders {
    VISITOR_PARKING = 'Visitor Parking',
    SERVICE_REQUESTS = 'Service Requests',
    BOOKINGS = 'Bookings',
    REPORTED_VIOLATIONS = 'Reported Violations',
    ANNOUNCEMENTS = 'Announcements'
  }

  // const tableHeaders = {
  //   'Visitor Parking': ['Parking Start', 'Parking End', 'Car', 'License Plate', 'Visitor For', 'Unit', 'Status', 'Quick Actions'],
  //   'Service Requests': ['Date', 'Title', 'Unit', 'Priority', 'Status'],
  //   'Bookings': ['Date', 'Type', 'Booking Date', 'Booking Time', 'Booking User', 'Unit', 'Status', 'Quick Actions'],
  //   'Reported Violations': ['Date', 'Title', 'Stage', 'Type', 'Due Date', 'Quick Actions'],
  //   'Announcements': ['Date', 'Title', 'Description', 'Quick Actions']
  // };

  // const tableData = {
  //   'Visitor Parking': allVisitorParking,
  //   'Service Requests': allServiceRequests,
  //   'Bookings': allBookings,
  //   'Reported Violations': allViolations,
  //   'Announcements': allAnnouncements
  // }

  const getIcon = (title: TableHeaders) => {
    switch (title) {
      case TableHeaders.VISITOR_PARKING:
        return LocalParking;
      case TableHeaders.SERVICE_REQUESTS:
        return Handyman;
      case TableHeaders.BOOKINGS:
        return Event;
      case TableHeaders.REPORTED_VIOLATIONS:
        return CrisisAlert;
      case TableHeaders.ANNOUNCEMENTS:
        return Campaign;
    }
  };

  const handleInfoCardClick = (title: string) => {
    if (title === 'Active Announcement') {
      navigate('/announcements');
    } else if (title === 'Open Service Requests') {
      navigate('/serviceRequests');
    } else if (title === 'Open Violations') {
      navigate('/violations');
    } else if (title === 'Active Visitor Parking') {
      navigate('/visitorParkings');
    } else if (title === 'Upcoming Bookings') {
      navigate('/bookings');
    }
  };

  const buildInfoCard = (icon: OverridableComponent<SvgIconTypeMap<{}, "svg">>, title: string, data: any) => {
    return (
      <Paper onClick={() => handleInfoCardClick(title)} elevation={3} sx={{ cursor: 'pointer', padding: 1, borderRadius: 3, textAlign: 'center', background: '#f4f4f4' }}>
        <Paper elevation={5} sx={{ paddingInline: 1, borderRadius: 6, display: 'flex', justifyContent: 'space-between'}}>
          {React.createElement(icon, { sx: { fontSize: 40, color: '#3f51b5', margin: 'auto 0' } })}
          <Typography variant="h6" sx={{maxWidth: 150, fontWeight: 600, textAlign: 'start'}}>{title}</Typography>
        </Paper>
        <Typography variant="h5" sx={{marginBlock: 4}}>{data}</Typography>
      </Paper>
    );
  }

  const addButtonClicked = (title: string) => {
      if (title === 'Visitor Parking') {
          navigate('/requestVisitorParking');
      } else if (title === 'Service Requests') {
          navigate('/createServiceRequest');
      } else if (title === 'Bookings') {
          navigate('/createBooking');
      } else if (title === 'Reported Violations') {
          navigate('/createViolation');
      } else if (title === 'Announcements') {
          navigate('/createAnnouncement');
      }
  };

  const viewAllButton = (title: string) => {
      if (title === 'Visitor Parking') {
          navigate('/visitorParkings');
      } else if (title === 'Service Requests') {
          navigate('/serviceRequests');
      } else if (title === 'Bookings') {
          navigate('/bookings');
      } else if (title === 'Reported Violations') {
          navigate('/violations');
      } else if (title === 'Announcements') {
          navigate('/announcements');
      }
  };

  const buildTable = (title: TableHeaders, data: Announcements[] | Booking[] | ViewServiceRequestData[] | Violation[] | VisitorParkingRequest[] ) => {
    return (
      <>
        {title === 'Announcements' && <BuildAnnouncementTable item={data as Announcements[]} />}
        {title === 'Bookings' && <BuildBookingsTable item={data as Booking[]} />}
        {title === 'Service Requests' && <BuildServiceRequestsTable item={data as ViewServiceRequestData[]} />}
        {title === 'Reported Violations' && <BuildViolationsTable item={data as Violation[]} />}
        {title === 'Visitor Parking' && <BuildVisitorParkingTable item={data as VisitorParkingRequest[]} />}
      </>
    )
  };

  const buildDashboardCard = (title: TableHeaders, data: any) => {
    return (
    <Paper sx={{ overflow: 'hidden', padding: 2, marginBottom: 3, background: '#f4f4f4', borderRadius: 3 }}>
        <Grid container spacing={2} justifyContent={'space-between'}>
            <Grid>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>{title}</Typography>
            </Grid>
            <Grid>
                <Tooltip title={`View All ${title}`}>
                    <IconButton
                        color="primary"
                        aria-label="view all"
                        sx={{ padding: 1 }}
                        onClick={() => viewAllButton(title)}
                    >
                        <OpenInFull fontSize="large"/>
                    </IconButton>
                </Tooltip>
                {TableHeaders.SERVICE_REQUESTS !== title && <Tooltip title={`Add ${title}`}>
                    <IconButton
                      color="primary"
                      aria-label="add"
                      sx={{ padding: 1 }}
                      onClick={() => addButtonClicked(title)}
                    >
                      <AddCircleOutlined fontSize="large"/>
                    </IconButton>
                </Tooltip>}
            </Grid>
        </Grid>
      <Divider sx={{ marginY: 3, background: 'black'}} />
      {buildTable(title, data)}
    </Paper>
    );
  };

  const buildMainDashboard = () => {
    return <>
      <Grid container spacing={3}>
        <Grid size={{xs: 12, sm: 6, md: 3}}>
          {buildInfoCard(Handyman, 'Open Service Requests', allServiceRequests.length)}
        </Grid>

        <Grid size={{xs: 12, sm: 6, md: 3}}>
          {buildInfoCard(Event, 'Upcoming Bookings', allBookings.length)}
        </Grid>

        <Grid size={{xs: 12, sm: 6, md: 3}}>
          {buildInfoCard(CrisisAlert, 'Open Violations', allViolations.length)}
        </Grid>

        <Grid size={{xs: 12, sm: 6, md: 3}}>
          {buildInfoCard(LocalParking, 'Active Visitor Parking', allVisitorParking.length)}
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ marginTop: '20px' }}>
        <Grid size={{xs: 12}}>
          {buildDashboardCard(TableHeaders.SERVICE_REQUESTS, allServiceRequests)}
        </Grid>
        <Grid size={{xs: 12}}>
          {buildDashboardCard(TableHeaders.BOOKINGS, allBookings)}
        </Grid>
        <Grid size={{xs: 12}}>
          {buildDashboardCard(TableHeaders.REPORTED_VIOLATIONS, allViolations)}
        </Grid>
        <Grid size={{xs: 12}}>
          {buildDashboardCard(TableHeaders.VISITOR_PARKING, allVisitorParking)}
        </Grid>
        <Grid size={{xs: 12}}>
          {buildDashboardCard(TableHeaders.ANNOUNCEMENTS, allAnnouncements)}
        </Grid>
      </Grid>
    </>
  };

  return (
    buildMainDashboard()
  );
};

export default Dashboard;
